import { useEffect, useState, useSyncExternalStore } from "react";

import { isSoftwareRenderer } from "./software-renderer.ts";

// What every ambient effect asks before it moves: does the reader want less
// motion, can this machine run WebGL, and what are the palette tokens in sRGB
// right now. Shaders cannot read CSS variables, so the tokens are resolved here.

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

function subscribeToQuery(query: string) {
  return (onChange: () => void) => {
    const list = window.matchMedia(query);
    list.addEventListener("change", onChange);
    return () => list.removeEventListener("change", onChange);
  };
}

const subscribeReducedMotion = subscribeToQuery(REDUCED_MOTION);
const readReducedMotion = () => window.matchMedia(REDUCED_MOTION).matches;
const serverReducedMotion = () => true;

/** Whether the reader asked for less motion. Effects keep their look and stop moving. */
export function useReducedMotion(): boolean {
  return useSyncExternalStore(subscribeReducedMotion, readReducedMotion, serverReducedMotion);
}

let webGlAnswer: boolean | undefined;

/** The renderer's real name where the browser gives it, else what it calls itself. */
function rendererName(context: WebGL2RenderingContext): string | null {
  const debug = context.getExtension("WEBGL_debug_renderer_info");
  const name: unknown = context.getParameter(debug ? debug.UNMASKED_RENDERER_WEBGL : context.RENDERER);
  return typeof name === "string" ? name : null;
}

/** Whether a WebGL context can be made and a graphics card draws it (D1337). Asked once per page. */
function canRunWebGl(): boolean {
  if (webGlAnswer !== undefined) return webGlAnswer;
  try {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("webgl2", { failIfMajorPerformanceCaveat: true });
    webGlAnswer = context !== null && !isSoftwareRenderer(rendererName(context));
    context?.getExtension("WEBGL_lose_context")?.loseContext();
  } catch {
    webGlAnswer = false;
  }
  return webGlAnswer;
}

/** Whether a shader should mount: a graphics card runs WebGL. Reduced motion still gets a still frame. */
export function useShaderSupport(): boolean {
  return useSyncExternalStore(subscribeNever, canRunWebGl, serverNoWebGl);
}

const subscribeNever = () => () => undefined;
const serverNoWebGl = () => false;

let probe: CanvasRenderingContext2D | null | undefined;

/** A CSS colour (oklch, color-mix…) as `rgba(r, g, b, a)`, which shaders accept. */
function toRgb(color: string): string {
  probe ??= document.createElement("canvas").getContext("2d", { willReadFrequently: true });
  if (!probe) return "rgba(0, 0, 0, 0)";
  probe.clearRect(0, 0, 1, 1);
  probe.fillStyle = color;
  probe.fillRect(0, 0, 1, 1);
  const [r = 0, g = 0, b = 0, a = 0] = probe.getImageData(0, 0, 1, 1).data;
  return `rgba(${r}, ${g}, ${b}, ${(a / 255).toFixed(3)})`;
}

function resolve(tokens: readonly string[]): string[] {
  const style = getComputedStyle(document.documentElement);
  return tokens.map((token) => toRgb(style.getPropertyValue(`--${token}`).trim()));
}

/** A palette token now, as four numbers: sRGB channels, and its opacity from 0 to 1. */
export function readTokenColor(token: string): readonly [number, number, number, number] {
  const [r = 0, g = 0, b = 0, a = 0] = (resolve([token])[0]?.match(/[\d.]+/g) ?? []).map(Number);
  return [r, g, b, a];
}

/**
 * Palette tokens (`glow-1`, `spark-2`…) in sRGB, read again when <html>'s
 * data-theme flips so a shader follows dark mode like the rest of the page.
 */
export function useTokenColors(tokens: readonly string[]): readonly string[] | undefined {
  const [colors, setColors] = useState<readonly string[]>();
  const key = tokens.join(" ");
  useEffect(() => {
    const names = key.split(" ");
    const update = () => setColors(resolve(names));
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => {
      observer.disconnect();
    };
  }, [key]);
  return colors;
}

/**
 * Everything a shader layer needs before it mounts: the palette in sRGB and
 * whether to hold still. Undefined while there is nothing to draw (no WebGL, or
 * only software to draw it, or the colours are not read yet); the layer then shows its painted fallback.
 */
export function useLiveShader(
  tokens: readonly string[],
): { readonly colors: string[]; readonly still: boolean } | undefined {
  const live = useShaderSupport();
  const still = useReducedMotion();
  const colors = useTokenColors(tokens);
  return live && colors ? { colors: [...colors], still } : undefined;
}

function subscribeScheme(onChange: () => void) {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
  return () => observer.disconnect();
}

const readScheme = (): "light" | "dark" =>
  document.documentElement.dataset["theme"] === "dark" ? "dark" : "light";
const serverScheme = (): "light" | "dark" => "light";

/** The scheme the page is painted in right now, for effects whose whole look changes at night. */
export function useColorScheme(): "light" | "dark" {
  return useSyncExternalStore(subscribeScheme, readScheme, serverScheme);
}
